window.SpotChicSearch = window.SpotChicSearch || (() => {
  const STORAGE_KEY = 'spot-chic-search-state-v2';

  const categoryMap = new Map([
    ['chemise', 'chemise'], ['chemises', 'chemise'],
    ['polo', 'polo'], ['polos', 'polo'],
    ['pull', 'pull'], ['pulls', 'pull'],
    ['veste', 'veste'], ['vestes', 'veste'],
    ['doudoune', 'doudoune'], ['doudounes', 'doudoune'],
    ['gilet', 'gilet'], ['gilets', 'gilet'],
    ['manteau', 'manteau'], ['manteaux', 'manteau'],
    ['capuche', 'capuche'], ['capuches', 'capuche'],
    ['short', 'short'], ['shorts', 'short'],
    ['pantalon', 'pantalon'], ['pantalons', 'pantalon'],
    ['jean', 'jean'], ['jeans', 'jean'],
    ['casquette', 'casquette'], ['casquettes', 'casquette'],
    ['chapeau', 'chapeau'], ['chapeaux', 'chapeau'],
    ['bonnet', 'bonnet'], ['bonnets', 'bonnet'],
  ]);

  const brandDefinitions = [
    ['Ralph Lauren', ['ralph lauren']],
    ['Tommy Hilfiger', ['tommy hilfiger', 'tommy']],
    ['Calvin Klein', ['calvin klein', 'ck']],
    ['Lacoste', ['lacoste']],
    ['U.S. Polo Assn.', ['u.s. polo assn.', 'u.s. polo assn', 'us polo assn', 'us polo', 'uspa']],
    ['Hugo Boss', ['hugo boss']],
    ['Boss Orange', ['boss orange']],
    ['Armani Exchange', ['armani exchange', 'a|x', 'ax']],
    ['Armani Jeans', ['armani jeans']],
    ['Armani', ['armani']],
    ['Michael Kors', ['michael kors']],
    ['Burberry', ['burberry']],
    ['Guess', ['guess']],
    ['Fred Perry', ['fred perry']],
    ['Harmont & Blaine', ['harmont & blaine', 'harmont blaine']],
    ['Brooks Brothers', ['brooks brothers']],
    ['Gant', ['gant']],
    ['Zara', ['zara']],
    ['Hackett', ['hackett']],
    ['Massimo Dutti', ['massimo dutti']],
    ['Paul & Shark', ['paul & shark', 'paul shark']],
    ['Farah', ['farah']],
  ];

  const stopWords = new Set([
    'a', 'au', 'aux', 'avec', 'de', 'des', 'du', 'en', 'et', 'homme', 'hommes',
    'la', 'le', 'les', 'pour', 'un', 'une',
  ]);

  const lexicalFamilies = new Map();
  [
    ['bleu', ['bleu', 'bleue', 'bleus', 'bleues']],
    ['blanc', ['blanc', 'blanche', 'blancs', 'blanches']],
    ['noir', ['noir', 'noire', 'noirs', 'noires']],
    ['vert', ['vert', 'verte', 'verts', 'vertes']],
    ['gris', ['gris', 'grise', 'grises']],
    ['violet', ['violet', 'violette', 'violets', 'violettes']],
    ['rouge', ['rouge', 'rouges']],
    ['rose', ['rose', 'roses']],
    ['jaune', ['jaune', 'jaunes']],
    ['beige', ['beige', 'beiges']],
    ['rayure', ['rayure', 'rayures']],
    ['carreau', ['carreau', 'carreaux']],
    ['manche', ['manche', 'manches']],
    ['court', ['court', 'courte', 'courts', 'courtes']],
  ].forEach(([canonical, variants]) => {
    variants.forEach((variant) => lexicalFamilies.set(variant, { canonical, variants }));
  });

  const sizeFamilies = new Map([
    ['xs', ['XS']], ['s', ['S']], ['m', ['M']], ['l', ['L']], ['xl', ['XL']], ['xxl', ['XXL']],
    ['xxxl', ['3XL', 'XXXL']], ['3xl', ['3XL', 'XXXL']], ['lg', ['L/G']], ['lxl', ['L/XL']],
  ]);

  const normalizeWord = (value) => value.toLocaleLowerCase('fr').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '');
  const tokenize = (value) => value.trim().split(/\s+/).map((original, index) => ({ original, normalized: normalizeWord(original), index })).filter((token) => token.normalized);
  const aliases = brandDefinitions.flatMap(([canonical, items]) => items.map((alias) => ({ canonical, tokens: tokenize(alias).map((token) => token.normalized) }))).filter((alias) => alias.tokens.length).sort((a, b) => b.tokens.length - a.tokens.length);
  const quoteValue = (value) => `"${String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  const titleOrBodyClause = (value) => { const quoted = quoteValue(value); return `(title:${quoted} OR body:${quoted})`; };

  const findBrand = (tokens) => {
    for (const alias of aliases) {
      for (let start = 0; start <= tokens.length - alias.tokens.length; start += 1) {
        if (alias.tokens.every((part, offset) => tokens[start + offset]?.normalized === part)) {
          return { canonical: alias.canonical, tokenIndexes: new Set(tokens.slice(start, start + alias.tokens.length).map((token) => token.index)) };
        }
      }
    }
    return null;
  };

  const parseQuery = (rawQuery) => {
    const tokens = tokenize(rawQuery);
    const brand = findBrand(tokens);
    const brandTokenIndexes = brand?.tokenIndexes || new Set();
    let category = null;
    let categoryTokenIndex = -1;
    for (const token of tokens) {
      if (brandTokenIndexes.has(token.index)) continue;
      if (categoryMap.has(token.normalized)) { category = categoryMap.get(token.normalized); categoryTokenIndex = token.index; break; }
    }
    return { tokens, category, categoryTokenIndex, brand, brandTokenIndexes };
  };

  const isControlledQuery = (rawQuery) => { const { category, brand } = parseQuery(rawQuery); return Boolean(category || brand); };
  const buildLexicalClause = (token) => {
    const family = lexicalFamilies.get(token.normalized);
    if (!family) return titleOrBodyClause(token.original);
    return `(${family.variants.map((variant) => titleOrBodyClause(variant)).join(' OR ')})`;
  };
  const buildSizeClause = (token) => {
    const sizes = sizeFamilies.get(token.normalized);
    if (!sizes) return null;
    const clauses = sizes.flatMap((size) => [`title:${quoteValue(`Taille ${size}`)}`, `body:${quoteValue(`Taille ${size}`)}`]);
    return `(${clauses.join(' OR ')})`;
  };
  const buildBrandClause = (canonical) => canonical === 'U.S. Polo Assn.'
    ? `(title:${quoteValue('U.S. Polo Assn.')} OR title:${quoteValue('US POLO ASSN')})`
    : `title:${quoteValue(canonical)}`;

  const buildSearchQuery = (rawQuery) => {
    const { tokens, category, categoryTokenIndex, brand, brandTokenIndexes } = parseQuery(rawQuery);
    if (!tokens.length) return '';
    if (!category && !brand) return rawQuery.trim();
    const clauses = [];
    if (category) clauses.push(`title:${category}*`);
    if (brand) clauses.push(buildBrandClause(brand.canonical));
    tokens.forEach((token) => {
      if (token.index === categoryTokenIndex || brandTokenIndexes.has(token.index) || stopWords.has(token.normalized)) return;
      clauses.push(buildSizeClause(token) || buildLexicalClause(token));
    });
    return clauses.join(' ');
  };

  const buildPredictiveQuery = (rawQuery) => rawQuery.trim();
  const saveState = (displayQuery, technicalQuery, controlled) => { try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ displayQuery, technicalQuery, controlled })); } catch (error) {} };
  const readState = () => { try { const rawState = sessionStorage.getItem(STORAGE_KEY); return rawState ? JSON.parse(rawState) : null; } catch (error) { return null; } };
  const getDisplayQuery = () => {
    const params = new URLSearchParams(window.location.search);
    const displayQuery = params.get('sc_q');
    if (displayQuery !== null) return displayQuery;
    const technicalQuery = params.get('q');
    const storedState = readState();
    return technicalQuery && storedState?.technicalQuery === technicalQuery && storedState?.controlled ? storedState.displayQuery || null : null;
  };
  const restoreDisplayQuery = (input) => { const displayQuery = getDisplayQuery(); if (displayQuery !== null) input.value = displayQuery; };

  const ensureHiddenInput = (form, name, value, dataAttribute = null) => {
    let input = dataAttribute ? form.querySelector(`input[${dataAttribute}]`) : form.querySelector(`input[name="${name}"]`);
    if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = name; if (dataAttribute) input.setAttribute(dataAttribute, 'true'); form.appendChild(input); }
    input.value = value;
    return input;
  };
  const enforceProductSearchOptions = (form) => { ensureHiddenInput(form, 'type', 'product'); ensureHiddenInput(form, 'options[unavailable_products]', 'hide'); };

  const prepareForm = (form, input) => {
    const rawQuery = input.value.trim();
    if (!rawQuery) return;
    enforceProductSearchOptions(form);
    const controlled = isControlledQuery(rawQuery);
    const technicalQuery = buildSearchQuery(rawQuery);
    saveState(rawQuery, technicalQuery, controlled);
    const existingTechnicalInput = form.querySelector('input[data-spot-chic-technical-query]');
    const prefixInput = form.querySelector('input[name="options[prefix]"]');
    if (!controlled) {
      input.name = 'q';
      existingTechnicalInput?.remove();
      if (prefixInput) prefixInput.value = 'last';
      return;
    }
    const technicalInput = ensureHiddenInput(form, 'q', technicalQuery, 'data-spot-chic-technical-query');
    technicalInput.value = technicalQuery;
    input.name = 'sc_q';
    if (prefixInput) prefixInput.value = 'none';
  };

  const preserveDisplayQuery = (searchParams) => {
    const params = new URLSearchParams(searchParams);
    params.set('type', 'product');
    params.set('options[unavailable_products]', 'hide');
    const displayQuery = getDisplayQuery();
    if (displayQuery !== null) { params.set('sc_q', displayQuery); if (params.has('q')) params.set('options[prefix]', 'none'); }
    return params.toString();
  };

  const enhanceSearchLink = (anchor) => {
    if (!anchor?.href) return;
    const url = new URL(anchor.href, window.location.origin);
    if (url.pathname !== '/search' && !url.pathname.endsWith('/search')) return;
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[unavailable_products]', 'hide');
    const displayQuery = getDisplayQuery();
    if (displayQuery !== null && url.searchParams.has('q')) { url.searchParams.set('sc_q', displayQuery); url.searchParams.set('options[prefix]', 'none'); }
    anchor.href = `${url.pathname}${url.search}${url.hash}`;
  };

  const updateNoResultsMessage = () => {
    const emptySearch = document.querySelector('.template-search--empty');
    const displayQuery = getDisplayQuery();
    const status = emptySearch?.querySelector('.template-search__header p[role="status"]');
    if (displayQuery && status) status.textContent = `Aucun résultat trouvé pour « ${displayQuery} ». Vérifiez l’orthographe ou essayez avec un autre mot ou une autre expression.`;
  };
  const refreshPresentation = () => {
    document.querySelectorAll('form[action*="/search"] input[type="search"]').forEach(restoreDisplayQuery);
    document.querySelectorAll('form[action*="/search"]').forEach(enforceProductSearchOptions);
    document.querySelectorAll('.template-search a[href*="/search"], .facets-container a[href*="/search"]').forEach(enhanceSearchLink);
    updateNoResultsMessage();
  };

  document.addEventListener('click', (event) => { const link = event.target.closest('.template-search a[href*="/search"], .facets-container a[href*="/search"]'); if (link) enhanceSearchLink(link); }, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refreshPresentation, { once: true }); else queueMicrotask(refreshPresentation);

  return { buildSearchQuery, buildPredictiveQuery, enhanceSearchLink, getDisplayQuery, isControlledQuery, preserveDisplayQuery, prepareForm, refreshPresentation, restoreDisplayQuery, updateNoResultsMessage };
})();

customElements.whenDefined('facet-filters-form').then(() => {
  const FacetFiltersFormClass = customElements.get('facet-filters-form');
  if (!FacetFiltersFormClass || FacetFiltersFormClass.spotChicSearchPatchedV2) return;
  const originalRenderPage = FacetFiltersFormClass.renderPage;
  if (typeof originalRenderPage === 'function') FacetFiltersFormClass.renderPage = function(searchParams, event, updateURLHash = true) { return originalRenderPage.call(this, window.SpotChicSearch.preserveDisplayQuery(searchParams), event, updateURLHash); };
  const originalRenderSection = FacetFiltersFormClass.renderSection;
  if (typeof originalRenderSection === 'function') FacetFiltersFormClass.renderSection = function(html, event, updateEvent) { const result = originalRenderSection.call(this, html, event, updateEvent); window.SpotChicSearch.refreshPresentation(); return result; };
  const originalStartUpdateEvent = FacetFiltersFormClass.startUpdateEvent;
  if (typeof originalStartUpdateEvent === 'function') FacetFiltersFormClass.startUpdateEvent = function(searchParams) { const params = new URLSearchParams(searchParams); const displayQuery = params.get('sc_q'); if (displayQuery !== null) params.set('q', displayQuery); return originalStartUpdateEvent.call(this, params.toString()); };
  FacetFiltersFormClass.spotChicSearchPatchedV2 = true;
});

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    if (this.input) {
      window.SpotChicSearch.restoreDisplayQuery(this.input);
      window.SpotChicSearch.refreshPresentation();
      this.input.form.addEventListener('submit', this.onSearchSubmit.bind(this));
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener('input', debounce((event) => this.onChange(event), 300).bind(this));
    }
  }
  onSearchSubmit() { if (!this.input.value.trim() || this.querySelector('[aria-selected="true"] a')) return; window.SpotChicSearch.prepareForm(this.input.form, this.input); }
  toggleResetButton() { const hidden = this.resetButton.classList.contains('hidden'); if (this.input.value.length > 0 && hidden) this.resetButton.classList.remove('hidden'); else if (this.input.value.length === 0 && !hidden) this.resetButton.classList.add('hidden'); }
  onChange() { this.toggleResetButton(); }
  shouldResetForm() { return !document.querySelector('[aria-selected="true"] a'); }
  onFormReset(event) { event.preventDefault(); if (this.shouldResetForm()) { this.input.value = ''; this.input.focus(); this.toggleResetButton(); } }
}
customElements.define('search-form', SearchForm);
